import {
  Action, CircularGaugeChart, ExpandableContent, FontWeight, Grid, HStack, Icon, ICON_ACCOUNT_MULTIPLE_OUTLINE, ICON_BULLSEYE_ARROW, ICON_CHART_LINE, Message, Stack, Text,
} from '@doveit/bricks';
import React from 'react';
import Markdown from 'react-markdown';
import { GoalMetric, ManagerGoal, TimeRange } from '../../../providers/api/dtos';
import {
  configByRating
  ,
} from './mapper';
import { getPerformanceDetails } from './utils/getPerformanceDetails';
import { PerformanceActionType } from './utils/getPerformanceAction';
import ViewAgentSmallPreview from '../../containers/view-agent-small-preview/ViewAgentSmallPreview';
import { getAgentName } from '../../utils/utils';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import EditManagerGoalAction from '../../containers/edit-manager-goal-action/EditManagerGoalAction';

export interface ManagerGoalPerformanceDetailViewProps extends React.AriaAttributes {
  metric: GoalMetric,
  timeRange: TimeRange,
  goal: ManagerGoal,
  onNewGoalAccepted?: (goal: ManagerGoal) => void,
}

const ManagerGoalPerformanceDetailView: React.FC<ManagerGoalPerformanceDetailViewProps> = ({
  metric,
  timeRange,
  goal,
  onNewGoalAccepted,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');

  const {
    title, status, message, performanceDescription, progress, targetDescription, action,
  } = React.useMemo(() => getPerformanceDetails(metric, goal, timeRange), [goal, metric, timeRange]);
  const { color, hint } = configByRating[status];

  const agentsBelowTarget = (goal.metrics[metric].yearly.issues || [])
    ?.filter(({ type }) => type === 'BELOW_TARGET')
    .map(({ agent, value }) => ({ agent, value }));

  return (
    <Stack gap={200} {...rest}>
      <HStack gap={300}>
        <CircularGaugeChart
          progress={progress}
          size="L"
          color={hint.messageType}
        />
        <Text.H4 fontWeight={500} color={color}>
          {title}
        </Text.H4>
      </HStack>

      <HStack gap={300} wrap="nowrap">
        {!isMobile && (
          <div style={{ flexShrink: 0 }}>
            <Icon
              path={ICON_CHART_LINE}
              size={40}
              color="neutral.default.highest"
            />
          </div>
        )}

        <Text.Body fontWeight={FontWeight.LIGHT}>
          <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
            {performanceDescription}
          </Markdown>
        </Text.Body>
      </HStack>

      {targetDescription && (
        <HStack gap={300} wrap="nowrap">
          {!isMobile && (
            <div style={{ flexShrink: 0 }}>
              <Icon
                path={ICON_BULLSEYE_ARROW}
                size={40}
                color="neutral.default.highest"
              />
            </div>
          )}

          <Text.Body fontWeight={FontWeight.LIGHT}>
            <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
              {targetDescription}
            </Markdown>
          </Text.Body>
        </HStack>
      )}

      {agentsBelowTarget.length > 0 && (
        <HStack
          gap={300}
          wrap="nowrap"
          alignItems="start"
        >
          {!isMobile && (
            <div style={{ flexShrink: 0 }}>
              <Icon
                path={ICON_ACCOUNT_MULTIPLE_OUTLINE}
                size={40}
                color="neutral.default.highest"
              />
            </div>
          )}

          <Stack gap={100}>
            <Text.Body fontWeight={FontWeight.LIGHT}>
              I seguenti agenti sono sotto l&apos;obiettivo dichiarato:
            </Text.Body>

            <Grid gutter={50}>
              {agentsBelowTarget
                .slice(0, 6)
                .map(({ agent, value }) => (
                  <Grid.Unit key={agent.id} size={1 / Math.min(3, Math.ceil(agentsBelowTarget.length / 2))}>
                    <ViewAgentSmallPreview
                      name={getAgentName(agent)}
                      details={`${(value * 100).toFixed()}% dell'obiettivo`}
                      size="S"
                    />
                  </Grid.Unit>
                ))}
            </Grid>

            {agentsBelowTarget.length > 6 && (
              <ExpandableContent openLabel="Mostra tutti">
                <Grid gutter={50}>
                  {agentsBelowTarget
                    .slice(6)
                    .map(({ agent, value }) => (
                      <Grid.Unit key={agent.id} size={1 / 3}>
                        <ViewAgentSmallPreview
                          name={getAgentName(agent)}
                          details={`${(value * 100).toFixed()}% dell'obiettivo`}
                          size="S"
                        />
                      </Grid.Unit>
                    ))}
                </Grid>
              </ExpandableContent>
            )}
          </Stack>
        </HStack>
      )}

      <Message
        type={message.type}
        message={message.title}
        icon={hint.icon}
      >
        {message.description}
      </Message>

      {action?.type === PerformanceActionType.EDIT_TARGET && (
        <div>
          <EditManagerGoalAction
            agentId={goal.agentId}
            onGoalAccepted={onNewGoalAccepted}
          >
            {({ openEditModal }) => (
              <Action
                label={action.label}
                color={action.color}
                size="S"
                emphasis="high"
                onClick={openEditModal}
                aria-label="Pulsante per modificare l'obiettivo di fatturato"
              />
            )}
          </EditManagerGoalAction>
        </div>
      )}
    </Stack>
  );
};

export default ManagerGoalPerformanceDetailView;
